import { Link } from "react-router-dom";
import {
  Home,
  Building2,
  Store,
  Warehouse,
  Factory,
  GraduationCap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { http } from "../../../axios/axios";
import { GetImageUrl } from "../../../helpers/GetImageUrl";
import { Helmet } from "react-helmet-async";

const API_BASE = import.meta.env.VITE_API_BASE || "http://localhost:8080";

function AboutPageSectionHeader({ badge, title, description, center = true }) {
  return (
    <div
      className={`max-w-5xl ${
        center ? "mx-auto text-center" : ""
      } mb-12 md:mb-16`}
    >
      {badge && (
        <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs md:text-sm           font-serif font-semibold uppercase tracking-[0.2em] text-blue-700">
          {badge}
        </span>
      )}

      <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight      font-serif ">
        {title}
      </h2>

      {description && (
        <p className="mt-5 max-w-2xl mx-auto text-slate-600 text-base md:text-lg leading-relaxed           font-serif">
          {description}
        </p>
      )}

      {/* Premium Accent Line */}
      <div className="mt-6 flex justify-center">
        <div className="h-1 w-24 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500" />
      </div>
    </div>
  );
}

function AboutUsPage() {
  const [aboutData, setAboutData] = useState({
    hero: null,
    stats: [],
    story: null,
    services: [],
    industries: [],
    closing: null,
    meta: null,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        heroRes,
        statsRes,
        storyRes,
        servicesRes,
        industriesRes,
        closingRes,
        metaRes,
      ] = await Promise.all([
        http.get("/about/hero"),
        http.get("/about/stats"),
        http.get("/about/story"),
        http.get("/about/services"),
        http.get("/about/industries"),
        http.get("/about/closing-section"),
        http.get("/about/meta"),
      ]);

      setAboutData({
        hero: heroRes.data.data,
        stats: statsRes.data.data || [],
        story: storyRes.data.data,
        services: servicesRes.data.data || [],
        industries: industriesRes.data.data || [],
        closing: closingRes.data.data,
        meta: metaRes.data.data,
      });
    } catch (error) {
      console.error("Error fetching About Us data:", error);
      setError("Failed to fetch About Us data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAboutData();
  }, []);

  const meta = aboutData.meta || {};
  const closingFeatures = aboutData.closing?.points || [];
  const closingFeatureMidpoint = Math.ceil(closingFeatures.length / 2);
  const closingFeatureColumns = [
    closingFeatures.slice(0, closingFeatureMidpoint),
    closingFeatures.slice(closingFeatureMidpoint),
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center">
          <div className="h-14 w-14 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>

          <p className="mt-5 text-lg font-medium text-gray-700">
            Loading About Page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{meta.metaTitle || "About Us"}</title>

        <meta
          name="description"
          content={
            meta.metaDescription ||
            "Learn more about our company, our story, and the services we offer."
          }
        />

        <meta
          name="keywords"
          content={
            meta.metaTags?.length
              ? meta.metaTags.join(", ")
              : "company, story, services"
          }
        />

        {meta.schemaScript && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: meta.schemaScript }}
          />
        )}
      </Helmet>
      <div className=" text-gray-800 mb-4">
        {/* ================= PREMIUM HERO ================= */}
        <section
          className="
    relative
    overflow-hidden
    bg-slate-950
    min-h-[auto]
    lg:min-h-[680px]

    2xl:min-h-[680px]
    flex
    items-center
  "
        >
          {/* Background */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="
      absolute
      inset-0
      bg-gradient-to-br
      from-blue-950
      via-slate-950
      to-black
    "
            />

            <div
              className="
        absolute
        -top-20
        -left-20
        h-52
        w-52
        sm:h-72
        sm:w-72
        lg:h-96
        lg:w-96
        bg-blue-600/20
        blur-3xl
        rounded-full
      "
            />

            <div
              className="
        absolute
        -bottom-20
        -right-20
        h-52
        w-52
        sm:h-72
        sm:w-72
        lg:h-96
        lg:w-96
        bg-cyan-500/10
        blur-3xl
        rounded-full
      "
            />
          </div>

          <div
            className="
      relative
      w-full
      px-4
      sm:px-6
      md:px-8
      lg:px-12
      xl:px-16
      2xl:px-18
      py-8
      sm:py-10
      lg:py-12
      xl:py-14
      2xl:py-16
    "
          >
            <div
              className="
        grid
        grid-cols-1
        lg:grid-cols-2
        gap-8
        xl:gap-12
        2xl:gap-16
        items-center
        h-full
      "
            >
              {/* LEFT CONTENT */}
              <div
                className="
          order-2
          lg:order-1
          w-full
          text-center
          lg:text-left
        "
              >
                <span
                  className="
            inline-flex
            items-center
            px-4
            py-2
            rounded-full
            border
            border-blue-400/20
            bg-white/5
            backdrop-blur-md
            text-blue-200
            text-xs
            sm:text-sm
            font-medium
          "
                >
                  {aboutData.hero?.badge}
                </span>

                <h1
                  className="
            mt-5
            text-3xl
            sm:text-4xl
            md:text-5xl
            lg:text-5xl
            2xl:text-6xl
            font-bold
            text-white
            leading-tight
                      font-serif
          "
                >
                  {aboutData.hero?.title}

                  <span
                    className="
              block
              bg-gradient-to-r
              from-blue-400
              to-cyan-400
              bg-clip-text
              text-transparent
                        font-serif
            "
                  >
                    {aboutData.hero?.highlightedTitle}
                  </span>
                </h1>

                <p
                  className="
            mt-4
            text-sm
            sm:text-base
            md:text-lg
            xl:text-xl
            text-slate-300
            leading-relaxed
                      font-serif
          "
                >
                  {aboutData.hero?.description}
                </p>

                <div
                  className="
            flex
            flex-col
            sm:flex-row
            gap-3
            mt-7
            justify-center
            lg:justify-start
          "
                >
                  <Link
                    to={aboutData.hero?.primaryButton?.link}
                    className="
              w-full
              sm:w-auto
              px-6
              py-3
              rounded-xl
              bg-blue-600
              hover:bg-blue-700
              text-white
              font-semibold
              shadow-lg
              transition
                        font-serif
            "
                  >
                    {aboutData.hero?.primaryButton?.text}
                  </Link>

                  <Link
                    to={aboutData.hero?.secondaryButton?.link}
                    className="
              w-full
              sm:w-auto
              px-6
              py-3
              rounded-xl
              border
              border-white/20
              bg-white/5
              backdrop-blur-md
              text-white
              transition
              hover:bg-white/10
                        font-serif
            "
                  >
                    {aboutData.hero?.secondaryButton?.text}
                  </Link>

                  <Link
                    to="/it-products"
                    className="w-full rounded-xl border border-cyan-300/40 bg-cyan-300/10 px-6 py-3 font-semibold text-cyan-100 transition hover:bg-cyan-300/20 sm:w-auto"
                  >
                    View IT Products
                  </Link>
                </div>
              </div>

              {/* RIGHT IMAGE */}
              <div
                className="
          order-1
          lg:order-2
          relative
          w-full
        "
              >
                <div
                  className="
            w-full
            rounded-3xl
            overflow-hidden
            border
            border-white/10
            bg-white/5
            backdrop-blur-xl
            p-2
            sm:p-3
            shadow-[0_20px_60px_rgba(0,0,0,0.35)]
          "
                >
                  <img
                    src={GetImageUrl(aboutData.hero?.image)}
                    alt="Security Surveillance"
                    className="
              w-full
              h-auto
              max-w-full
              block
              object-contain
              object-center
              lg:h-[360px]
              xl:h-[420px]
              2xl:h-[500px]
              lg:object-cover
              rounded-2xl
            "
                  />
                </div>

                {/* FLOATING CARD */}

                <div
                  className="
            absolute
            bottom-3
            left-3
            sm:bottom-5
            sm:left-5
            lg:bottom-6
            lg:left-6
            bg-white
            rounded-2xl
            p-3
            sm:p-4
            shadow-xl
            w-[150px]
            sm:w-[200px]
            lg:w-[230px]
          "
                >
                  <h4
                    className="
              text-xl
              sm:text-2xl
              font-bold
              text-blue-600
                        font-serif
            "
                  >
                    {aboutData.hero?.floatingStat?.value}
                  </h4>

                  <p
                    className="
              text-xs
              sm:text-sm
              text-slate-600
              mt-1
                        font-serif
            "
                  >
                    {aboutData.hero?.floatingStat?.label}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* ================= STATS ================= */}
        <section
          className="
    bg-white
    py-12
    sm:py-14
    lg:py-16
    xl:py-20
  "
          aria-labelledby="company-stats-heading"
        >
          <div
            className="
      w-full
      px-4
      sm:px-6
      md:px-8
      lg:px-12
      xl:px-16
      2xl:px-20
    "
          >
            {/* SEO Heading */}
            <div className="text-center mb-10 lg:mb-14">
              <h2
                id="company-stats-heading"
                className="
          text-2xl
          sm:text-3xl
          md:text-4xl
          lg:text-5xl
          font-bold
          text-slate-900
          font-serif
        "
              >
                Our Achievements
              </h2>

              <p
                className="
          mt-3
          text-sm
          sm:text-base
          lg:text-lg
          text-slate-600
                    font-serif
        "
              >
                Trusted security solutions delivered with experience and
                excellence.
              </p>
            </div>

            <div
              className="
        grid
        grid-cols-1
        sm:grid-cols-2
        lg:grid-cols-4
        gap-5
        sm:gap-6
        xl:gap-8
      "
            >
              {aboutData.stats?.map((item) => (
                <article
                  key={item._id}
                  className="
            bg-white
            rounded-3xl
            border
            border-slate-200
            p-5
            sm:p-6
            lg:p-8
            text-center
            shadow-sm
            hover:shadow-xl
            hover:-translate-y-1
            transition-all
            duration-300
          "
                >
                  <data
                    value={item.value}
                    className="
              block
              text-3xl
              sm:text-4xl
              lg:text-5xl
              font-bold
              text-blue-600
            "
                  >
                    {item.value}
                  </data>

                  <p
                    className="
              mt-3
              text-sm
              sm:text-base
              lg:text-lg
              text-slate-600
              font-medium
              leading-relaxed
            "
                  >
                    {item.label}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
        {/* ================= COMPANY STORY ================= */}
        <section
          className="
                      py-12
                      sm:py-14
                      lg:py-16
                      xl:py-8
                    "
          aria-labelledby="our-story-heading"
        >
          <div
            className="
                        w-full
                        px-4
                        sm:px-6
                        md:px-8
                        lg:px-12
                        xl:px-16
                        2xl:px-20
                      "
          >
            {/* Section Header */}
            <header className="w-full">
              <AboutPageSectionHeader
                badge={aboutData.story?.badge || "Our Story"}
                title={aboutData.story?.title || ""}
                description={aboutData.story?.description || ""}
              />
            </header>

            <article
              className="
                          grid
                          grid-cols-1
                          lg:grid-cols-2
                          gap-8
                          xl:gap-14
                          items-center
                          mt-10
                          lg:mt-14
                        "
            >
              {/* Image */}
              <figure className="w-full">
                <img
                  src={GetImageUrl(aboutData.story?.image)}
                  alt={
                    aboutData.story?.heading ||
                    "Company team and security solutions"
                  }
                  loading="lazy"
                  style={{ height: "550px", width: "650px" }}
                  className="
                              w-full
                              h-auto
                              max-w-full
                              block
                              object-contain
                              object-center
                              rounded-3xl
                              lg:rounded-[40px]
                              shadow-xl
                            "
                />
              </figure>

              {/* Content */}
              <div className="w-full">
                <h2
                  id="our-story-heading"
                  className="
            text-2xl
            sm:text-3xl
            lg:text-4xl
            font-bold
            text-slate-900
            leading-tight
          "
                >
                  {aboutData.story?.heading}
                </h2>

                <div className="mt-5 space-y-4">
                  <p
                    className="
              text-sm
              sm:text-base
              md:text-lg
              text-slate-600
              leading-relaxed
            "
                  >
                    {aboutData.story?.paragraphOne}
                  </p>

                  <p
                    className="
              text-sm
              sm:text-base
              md:text-lg
              text-slate-600
              leading-relaxed
            "
                  >
                    {aboutData.story?.paragraphTwo}
                  </p>
                </div>

                {/* Highlights */}
                <ul
                  className="
            mt-6
            grid
            grid-cols-1
            sm:grid-cols-2
            gap-3
          "
                >
                  {aboutData.story?.highlights?.map((item, index) => (
                    <li
                      key={index}
                      className="
                flex
                items-center
                gap-3
                rounded-xl
                bg-white
                border
                border-slate-200
                px-4
                py-3
                shadow-sm
                hover:shadow-md
                transition
              "
                    >
                      <span
                        className="
                  h-2
                  w-2
                  shrink-0
                  rounded-full
                  bg-blue-600
                "
                      />

                      <span
                        className="
                  text-sm
                  sm:text-base
                  text-slate-700
                  font-medium
                "
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </div>
        </section>
        {/* ================= SERVICES ================= */}
        <section
          className="
    bg-white
    py-12
    sm:py-14
    lg:py-16
    xl:py-18
    px-4
    sm:px-6
    lg:px-12
    xl:px-16
    2xl:px-20
  "
          aria-labelledby="services-heading"
        >
          <div className="w-full">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {(aboutData.services?.sections?.length ? aboutData.services.sections : [aboutData.services].filter(Boolean)).map((section, sectionIndex) => (
                <div
                  key={section._id || sectionIndex}
                  className={sectionIndex === 0
                    ? "lg:border-r-2 lg:border-blue-500 lg:pr-8"
                    : "border-t-2 border-blue-500 pt-8 lg:border-t-0 lg:pl-8 lg:pt-0"}
                >
                  <AboutPageSectionHeader badge={section.badge || "What We Offer"} title={section.title || "Our Services"} description={section.description || ""} />
                  <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {(section.items || []).map((service, index) => (
                      <article key={service._id || index} className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl sm:p-6">
                        <h3 className="text-lg font-semibold text-slate-900 transition group-hover:text-blue-600 sm:text-xl">{service.title}</h3>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{service.description}</p>
                      </article>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* ================= WHY TRUST US ================= */}
        <section
          className="
    bg-white
    py-12
    sm:py-16
    lg:py-20
    xl:py-20
    px-4
    sm:px-6
    md:px-8
    lg:px-12
    xl:px-16
    2xl:px-20
  "
          aria-labelledby="industries-heading"
        >
          <div className="w-full">
            {/* Section Header */}
            <header>
              <AboutPageSectionHeader
                badge={aboutData.industries?.badge || "Why Clients Choose Us"}
                title={
                  aboutData.industries?.title || "Your Trusted Security Partner"
                }
                description={aboutData.industries?.description || ""}
              />
            </header>

            <div
              className="
        mt-10
        lg:mt-14
        grid
        grid-cols-1
        sm:grid-cols-2
        lg:grid-cols-4
        gap-5
        sm:gap-6
        xl:gap-8
      "
            >
              {aboutData.industries?.items?.map((item, index) => (
                <article
                  key={item._id || index}
                  className="
            group
            relative
            bg-slate-50
            rounded-3xl
            p-5
            sm:p-6
            lg:p-8
            min-h-[120px]
            flex
            items-center
            border
            border-slate-200
            shadow-sm
            hover:bg-blue-600
            hover:-translate-y-2
            hover:shadow-xl
            transition-all
            duration-300
            overflow-hidden
          "
                >
                  {/* Accent Line */}
                  <span
                    className="
              absolute
              top-0
              left-0
              w-full
              h-1
              rounded-t-3xl
              bg-gradient-to-r
              from-blue-600
              to-cyan-500
              opacity-0
              group-hover:opacity-100
              transition-opacity
              duration-300
            "
                  />

                  <h3
                    id={index === 0 ? "industries-heading" : undefined}
                    className="
              text-base
              sm:text-lg
              lg:text-xl
              font-semibold
              leading-snug
              text-slate-800
              group-hover:text-white
              transition-colors
              duration-300
            "
                  >
                    {item.title}
                  </h3>
                </article>
              ))}
            </div>
          </div>
        </section>
        {/* ================= PREMIUM CTA ================= */}
        <section
          className="
    py-12
    sm:py-16
    lg:py-16
    px-4
    sm:px-6
    lg:px-12
    xl:px-16
    2xl:px-16
  "
          aria-labelledby="closing-section-heading"
        >
          <div className="w-full">
            <article
              className="
        relative
        overflow-hidden
        rounded-[28px]
        sm:rounded-[32px]
        lg:rounded-[40px]
        bg-gradient-to-br
        from-blue-950
        via-blue-900
        to-slate-900
        p-6
        sm:p-8
        lg:p-12
        xl:p-14
        shadow-[0_25px_70px_rgba(0,0,0,0.18)]
      "
            >
              {/* Background Glow */}
              <div
                className="
          absolute
          -top-20
          -left-20
          h-52
          w-52
          sm:h-72
          sm:w-72
          rounded-full
          bg-blue-500/20
          blur-3xl
        "
              />

              <div
                className="
          absolute
          -bottom-20
          -right-20
          h-52
          w-52
          sm:h-72
          sm:w-72
          rounded-full
          bg-cyan-500/20
          blur-3xl
        "
              />

              <div className="relative">
                <div
                  className={`grid grid-cols-1 items-center gap-8 xl:gap-12 ${
                    aboutData.closing?.showCard !== false &&
                    aboutData.closing?.cardImage?.url
                      ? "lg:grid-cols-2"
                      : "lg:grid-cols-1"
                  }`}
                >
                <div className="text-center lg:text-left">
                  <span
                    className="
              inline-flex
              items-center
              rounded-full
              border
              border-white/20
              bg-white/10
              px-4
              py-2
              text-xs
              sm:text-sm
              font-medium
              text-blue-100
              backdrop-blur-md
            "
                  >
                    {aboutData.closing?.badge}
                  </span>

                  <h2
                    id="closing-section-heading"
                    className="
              mt-5
              text-2xl
              sm:text-3xl
              md:text-4xl
              lg:text-5xl
              font-bold
              text-white
              leading-tight
            "
                  >
                    {aboutData.closing?.title}

                    <span
                      className="
                block
                bg-gradient-to-r
                from-blue-300
                to-cyan-300
                bg-clip-text
                text-transparent
              "
                    >
                      {aboutData.closing?.highlightedTitle}
                    </span>
                  </h2>

                  <p
                    className="
              mt-5
              text-sm
              sm:text-base
              lg:text-lg
              text-slate-300
              leading-relaxed
            "
                  >
                    {aboutData.closing?.description}
                  </p>

                  <div
                    className="
              mt-7
            flex
            flex-col
            items-center
            lg:items-start
            justify-center
            gap-4
          "
                  >
                    <Link
                      to={aboutData.closing?.button?.link || "/contact"}
                      className="
              w-full
              sm:w-fit
              px-7
              py-3.5
              rounded-2xl
              bg-white
              text-blue-900
              text-center
              font-semibold
              shadow-xl
              hover:scale-105
              transition-all
              duration-300
            "
                    >
                      {aboutData.closing?.button?.text}
                    </Link>

                    <p
                      className="
              text-xs
              sm:text-sm
              text-slate-400
                          text-center
            "
                    >
                      {aboutData.closing?.footerText ||
                        aboutData.closing?.bottomText}
                    </p>
                  </div>
                </div>

                {aboutData.closing?.showCard !== false &&
                  aboutData.closing?.cardImage?.url && (
                    <aside className="w-full overflow-hidden rounded-2xl border border-white/15 bg-white shadow-2xl">
                      <div className="w-full overflow-hidden bg-slate-100">
                        <img
                          src={GetImageUrl(
                            aboutData.closing.cardImage.url,
                          )}
                          alt={
                            aboutData.closing.cardImage.alt ||
                            "Suraksha 24X7 Free Security Consultation"
                          }
                          className="h-auto w-full object-cover"
                        />
                      </div>

                      <div className="p-5">
                        <Link
                          to={
                            aboutData.closing.cardButtonLink || "/contact"
                          }
                          className="inline-flex w-full items-center justify-center rounded-lg bg-blue-700 px-5 py-3 text-center font-semibold text-white transition-colors hover:bg-blue-800"
                        >
                          {aboutData.closing.cardButtonText ||
                            "Schedule a Free Consultation"}
                        </Link>
                      </div>
                    </aside>
                  )}
                </div>

                {closingFeatures.length > 0 && (
                  <div className="mt-10 grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2 md:gap-x-8">
                    {closingFeatureColumns.map((features, columnIndex) => (
                      <ul key={columnIndex} className="space-y-4">
                        {features.map((item, featureIndex) => (
                          <li
                            key={`${columnIndex}-${featureIndex}`}
                            className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200 sm:text-base"
                          >
                            <span
                              className="mt-0.5 text-cyan-300"
                              aria-hidden="true"
                            >
                              ✓
                            </span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ))}
                  </div>
                )}
              </div>
            </article>
          </div>
        </section>
      </div>
    </>
  );
}

export default AboutUsPage;
